import { Box, Button, Chip, Typography } from "@mui/joy";
import { alpha, darken } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useTour } from "@reactour/tour";
import { useDispatch, useSelector } from "react-redux";
import { Paper } from "../atoms/paper";
import { MissionMap, MissionMap_Entity } from "../entities/missionMap";
import {
  Engagement,
  Inspector,
  Interactor,
} from "../entities/missionMap/missionMap";
import {
  answersSelector,
  setAnswers,
  setSelectedToken,
} from "../redux/rhapsodyReducer";
import { blueBubble } from "./messages";
import { addToCalendar } from "./schedule";

export function ProjectsList({ mission }: { mission: MissionMap_Entity }) {
  const { setIsOpen } = useTour();
  const answers = useSelector(answersSelector);
  const dispatch = useDispatch();

  const missions = mission.Projects?.map((m) => new MissionMap(m));

  const engagements = missions.reduce<Engagement[]>((a, v) => {
    v.Engagements.forEach((e) => {
      e.Content.forEach((c) => {
        a.push(c);
      });
    });
    return a;
  }, []);

  const m = new MissionMap(mission);
  const sameInteractors = m.sameInteractors();

  const answerAll = (i: Interactor) => {
    const ret: {
      [token: string]: {
        inspectors?: {
          [i: number]: Inspector;
        };
        selectedInteractor?: Interactor;
        selectedInteractorJobStageID?: number;
      };
    } = {};

    missions.forEach((m) => {
      const inter = m.Interactors.find((e) => e.ID === i.ID);
      const inspectors = m?.Interactors.reduce<{
        [i: number]: Inspector;
      }>((a, v) => {
        a[v.ID] = {
          id: v.ID,
          title: v.Name,
          icon: v.Icon,
          kind: v.Kind,
        };
        return a;
      }, {});
      ret[m.PublicToken] = {
        inspectors,
        selectedInteractor: inter,
      };
    });

    dispatch(setAnswers(ret));
  };

  let hasAnswers = false;
  for (const key in answers) {
    if (Object.prototype.hasOwnProperty.call(answers, key)) {
      const answer = answers[key];
      if (
        answer.selectedInteractor?.SegueID &&
        answer.selectedInteractor?.Description !== "contract"
      )
        hasAnswers = true;
    }
  }

  return (
    <Paper sx={{ alignItems: "stretch" }}>
      <Box>
        <Typography level="title-lg">Projects</Typography>
        <Typography level="body-sm">
          {missions.length} Projects, {engagements.length} Services
        </Typography>
        <Box sx={{ mt: 1, display: "flex", gap: 1, justifyContent: "center" }}>
          {hasAnswers ? (
            <Chip
              variant="solid"
              onClick={() => {
                dispatch(setAnswers({}));
              }}
              endDecorator={<i className="fa-regular fa-delete-left"></i>}
            >
              Reset answers
            </Chip>
          ) : (
            <>
              {sameInteractors
                ?.sort((a, b) => a.ID - b.ID)
                ?.map((s) => {
                  return (
                    <Box
                      key={s.ID}
                      onClick={() => {
                        answerAll(s);
                      }}
                    >
                      <Chip
                        sx={{
                          cursor: "pointer",
                          color: "white",
                          background: s.Color,
                          "&:hover": {
                            background: darken(s.Color ?? "", 0.2),
                          },
                          "&:active": {
                            background: darken(s.Color ?? "", 0.2),
                          },
                        }}
                        startDecorator={<i className={s.Icon} />}
                      >
                        {s.Name} all
                      </Chip>
                    </Box>
                  );
                })}
            </>
          )}
        </Box>

        <DataGridPro
          autoHeight
          onRowClick={(r) => {
            dispatch(setSelectedToken(r.row.PublicToken));
            setIsOpen(false);
          }}
          headerHeight={0}
          sx={{ mt: 1 }}
          hideFooter
          getRowClassName={(r) => `${r.id}`}
          getRowId={(r) => r.PublicToken}
          rows={missions}
          columns={[
            {
              field: "ProjectLogo",
              headerName: "",
              width: 75,
              renderCell: (p) => (
                <Box
                  style={{
                    width: 50,
                    background: "#CFD8DD",
                    height: 50,
                    borderRadius: 8,
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    color: "#B0BEC5",
                    fontSize: 18,
                  }}
                >
                  {p.value ? (
                    <img src={p.value} style={{ width: 50, height: 50 }} />
                  ) : (
                    <i className="fa-regular fa-folder"></i>
                  )}
                </Box>
              ),
            },
            {
              field: "ProjectName",
              headerName: "Name",
              flex: 1,
              renderCell: (p) => (
                <Box sx={{ textAlign: "left" }}>
                  <Typography level="body-md">{p.value}</Typography>
                  <Typography level="body-xs">
                    {p.row.ScheduleDescription}
                  </Typography>
                </Box>
              ),
            },
            {
              field: "PublicToken",
              headerName: "",
              align: "right",
              width: 128,
              renderCell: (p) => {
                const stage = p.row.CurrentStage;
                let status;

                const allInspectors = answers[p.value]?.inspectors ?? [];
                const inspectors: Inspector[] = [];
                for (const key in allInspectors) {
                  if (
                    Object.prototype.hasOwnProperty.call(allInspectors, key)
                  ) {
                    const inspector = allInspectors[key];
                    inspectors.push(inspector);
                  }
                }

                const talkBack = inspectors?.find(
                  (i) => i.title === "Message" && i.message,
                );

                if (answers[p.value]?.selectedInteractor) {
                  const selectedInteractor =
                    answers[p.value]?.selectedInteractor;

                  status = (
                    <Typography
                      startDecorator={
                        <i className={selectedInteractor?.Icon}></i>
                      }
                      endDecorator={
                        talkBack ? (
                          <i className="fa-solid fa-comment"></i>
                        ) : undefined
                      }
                      level="body-sm"
                      color="primary"
                      sx={{ color: selectedInteractor?.Color }}
                    >
                      {selectedInteractor?.Name}
                    </Typography>
                  );
                }

                if (
                  stage &&
                  stage.Name !== "Invite" &&
                  stage?.Name !== "Contract" &&
                  !status
                ) {
                  status = (
                    <Typography
                      startDecorator={<img height={15} src={stage.Icon}></img>}
                      level="body-sm"
                      color="primary"
                      sx={{ color: stage.Color }}
                    >
                      {stage.Name}
                    </Typography>
                  );
                }

                if (!status && stage?.Name !== "Contract")
                  status = (
                    <Typography
                      endDecorator={
                        <i className="fa-solid fa-chevron-right"></i>
                      }
                      level="body-sm"
                      color="primary"
                    >
                      View & Answer
                    </Typography>
                  );

                let messages = <Box />;

                const unreadMessages = p.row
                  .allMessages()
                  ?.filter((m) => !m.Acknowledged);
                if (unreadMessages.length > 0) {
                  messages = (
                    <Typography
                      endDecorator={blueBubble}
                      color="primary"
                      level="body-xs"
                    >
                      {unreadMessages?.length} New Message
                      {unreadMessages.length > 1 ? "s" : ""}
                    </Typography>
                  );
                }
                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    {status}
                    {messages}
                  </Box>
                );
              },
            },
          ]}
        />
      </Box>
      <Button
        startDecorator={<i className="fa-solid fa-calendar-plus"></i>}
        variant="plain"
        color="neutral"
        fullWidth
        sx={{ mt: 1 }}
        onClick={() => addToCalendar(missions)}
      >
        Add to Calendar
      </Button>
    </Paper>
  );
}
